import React, { memo, useCallback, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';

import * as mainApprovalActions from '../actions/approval/main';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import { OnlineApprovalList } from '../components/OnlineApprovalLists';
import { Spinner } from '../components/helpers/Spinner';
import { showInformation } from '../utils/MessagePopup';
import { usePopupContext } from '../contexts/PopupContext';

const OnlineApprovalListsContainer = ({
  onlineApprovalLoading,
  onlineApproval,
  saving,
  history,
  match,
  loadPreviewOnlineApprovals,
  saveOnlineApprovals,
  saveProgressOnlineApprovals,
  updateOnlineApprovals,
}) => {
  const { openPopup } = usePopupContext();

  const prepareSaveOnlineApprovalsParams = useCallback(
    (data, approvingUser) => {
      const searchParams = new URLSearchParams(window.location.search);
      const token = searchParams.get('token');

      const body = {
        token,
        list: data,
        completedByFirstName: approvingUser.firstName,
        completedByLastName: approvingUser.lastName,
      };

      return {
        projectId: match.params.projectId,
        approvalId: match.params.approvalId,
        body,
      };
    },
    [match],
  );

  const loadApprovalsData = useCallback(() => {
    loadPreviewOnlineApprovals(
      match.params.projectId,
      match.params.approvalId,
      history.location.search,
      'online',
    );
  }, [loadPreviewOnlineApprovals, match, history]);

  useEffect(() => {
    loadApprovalsData();
  }, []);

  const handleSubmit = useCallback(
    (data, approvingUser) => {
      const { projectId, approvalId, body } = prepareSaveOnlineApprovalsParams(data, approvingUser);

      const afterSuccess = () => {
        showInformation(openPopup, 'Approvals were submitted and received successfully');
        loadApprovalsData();
      };

      saveOnlineApprovals({ projectId, approvalId, body, afterSuccess });
    },
    [prepareSaveOnlineApprovalsParams, saveOnlineApprovals, openPopup, loadApprovalsData],
  );

  const handleSaveProgress = useCallback(
    (data, approvingUser) => {
      const { projectId, approvalId, body } = prepareSaveOnlineApprovalsParams(data, approvingUser);

      const afterSuccess = () => {
        showInformation(openPopup, 'The progress has been saved');
      };

      saveProgressOnlineApprovals({ projectId, approvalId, body, afterSuccess });
    },
    [prepareSaveOnlineApprovalsParams, saveProgressOnlineApprovals, openPopup],
  );

  const handleUpdate = useCallback(
    data => {
      updateOnlineApprovals(data);
    },
    [updateOnlineApprovals],
  );

  return (
    <div className="full-height">
      <Helmet title="Generate Online Approval Lists" />

      {onlineApprovalLoading ? (
        <Spinner />
      ) : (
        <OnlineApprovalList
          onlineApproval={onlineApproval}
          onSaveProgress={handleSaveProgress}
          onSubmit={handleSubmit}
          onUpdate={handleUpdate}
          saving={saving}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  ...state.approval,
  onlineApprovalLoading: state.approval.common.get('onlineApprovalLoading'),
  saving: state.approval.common.get('onlineApprovalSaving'),
  onlineApproval: state.approval.common.get('onlineApproval'),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(mainApprovalActions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(memo(OnlineApprovalListsContainer));
